import _guardsMeQuery from "../__generated__/guardsMeQuery.graphql";
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Navigate, Outlet } from 'react-router-dom';
import { guardFor } from '../profile/access';
import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';

/**
 * Shared with `AppLayout`, which reads the same shape to set the kid/adult
 * theme attribute. One query, so a fetch either component triggers satisfies
 * both rather than racing two copies of it.
 */
export const MeQuery = (_guardsMeQuery.hash && _guardsMeQuery.hash !== "2bcd20423c11e1e52008429788b744d8" && console.error("The definition of 'guardsMeQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _guardsMeQuery);

/**
 * Everything behind the nav needs an account. A visitor who types `/history`
 * goes home rather than to a screen whose every query answers null.
 *
 * `network-only`: `logIn` and `setDefaultCharacter` both select only `ok`, so
 * neither mutation writes a fresh `me` into the store. Without forcing a
 * network read here, signing in would render this guard against the `me:
 * null` this same query already cached before the cookie existed, and bounce
 * straight back to `/signup` forever.
 *
 * `replace`, so the back button does not bounce the visitor between the guard
 * and the page it refused them.
 */
export function RequireAccount() {
  const data = useLazyLoadQuery<guardsMeQuery>(MeQuery, {}, {
    fetchPolicy: 'network-only'
  });
  if (guardFor(data.me) === 'signup') return <Navigate to="/signup" replace />;
  return <Outlet />;
}

/**
 * `/` and `/session` need a character chosen before they mean anything;
 * `/character` is the screen that fixes that, so it cannot sit behind this
 * guard itself — wrapping it too would send a learner with no character in a
 * loop back to the only screen that lets them pick one. That was this guard's
 * first version: it answered `homeRedirect(me)` and redirected there whenever
 * the answer wasn't `/`, which pointed `/character` at itself.
 *
 * `network-only` for the same reason as `RequireAccount`: `setDefaultCharacter`
 * selects only `ok`, so nothing invalidates the cached `me`. Without forcing a
 * refetch, choosing a character would read the pre-selection `me` this query
 * already cached and bounce straight back here.
 *
 * Amendment, 2026-10-01: it never did. Relay keys a query's cache entry on its
 * fetch policy and variables, not on the component (`QueryResource.js`,
 * `getQueryCacheIdentifier`), and `RequireAccount` stays mounted above both
 * `/character` and `/` holding that entry, so this read reused it and went
 * nowhere near the network. Every first pick bounced. What fixes it is
 * `setDefaultCharacter` returning the learner, which rewrites `me` in the
 * store; `//client:first_character_smoke_test` holds it.
 *
 * `guardFor` returns `"character"` only when `me.learner` exists with no
 * `defaultCharacter`; a learner-less account falls through unredirected here.
 * Safe only because this guard nests inside `RequireAccount` in
 * `routes.tsx`, which already sent that case to `/signup`.
 */
export function RequireCharacter() {
  const data = useLazyLoadQuery<guardsMeQuery>(MeQuery, {}, {
    fetchPolicy: 'network-only'
  });
  if (guardFor(data.me) === 'character') return <Navigate to="/character" replace />;
  return <Outlet />;
}